import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Row, Col, Card, CardBody, Button, Form, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    getAllBlogsAdmin,
    addBlog,
    updateBlog,
    deleteBlog,
    toggleBlogStatus,
    normalizeBlogAdminList,
} from '../../helper/blog_helper';
import ConfirmModal from '../../components/ConfirmModal';
import Header from '../../layouts/Header';
import Footer from '../../layouts/Footer';
import * as Utils from '../../Utils';
import AdminAddButton from '../../components/admin/AdminAddButton';
import { dataTableCustomStyles, dataTablePaginationOptions } from '../../components/admin/dataTableConfig';
import { Link } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DataTable from 'react-data-table-component';
import DataTableSkeleton from '../../components/DataTableSkeleton';
import moment from 'moment';
import AdminRowActions, { AdminActionButton } from '../../components/admin/AdminRowActions';
import AdminEmptyState from '../../components/admin/AdminEmptyState';

const QUILL_MODULES = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'image'],
        ['clean'],
    ],
};

const BlogEditor = () => {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentBlog, setCurrentBlog] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [formData, setFormData] = useState({
        title: '',
        desc: '',
        image: null,
        isActive: true,
    });
    const [imagePreview, setImagePreview] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [confirm, setConfirm] = useState(false);

    const fetchBlogs = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getAllBlogsAdmin();
            const fetchedBlogs = normalizeBlogAdminList(response);
            if (fetchedBlogs.length || response?.success !== false) {
                setBlogs(fetchedBlogs);
            } else {
                toast.error(response?.message || 'Failed to fetch blogs');
            }
        } catch (error) {
            toast.error('Error loading blogs');
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchBlogs();
    }, [fetchBlogs]);

    const filteredBlogs = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        return blogs.filter((blog) => {
            const matchesSearch = !query
                || (blog.title || '').toLowerCase().includes(query)
                || (blog.desc || '').toLowerCase().includes(query);
            const matchesStatus = filterStatus === 'all'
                || (filterStatus === 'active' && blog.isActive)
                || (filterStatus === 'inactive' && !blog.isActive);
            return matchesSearch && matchesStatus;
        });
    }, [blogs, searchText, filterStatus]);

    const stats = useMemo(() => ({
        total: blogs.length,
        active: blogs.filter((b) => b.isActive).length,
        inactive: blogs.filter((b) => !b.isActive).length,
    }), [blogs]);

    const handleShowModal = (blog = null) => {
        if (blog && blog._id) {
            setIsEditing(true);
            setCurrentBlog(blog);
            setFormData({
                title: blog.title || '',
                desc: blog.desc || '',
                image: null,
                isActive: blog.isActive,
            });
            setImagePreview(blog.image || null);
        } else {
            setIsEditing(false);
            setCurrentBlog(null);
            setFormData({
                title: '',
                desc: '',
                image: null,
                isActive: true,
            });
            setImagePreview(null);
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setIsEditing(false);
        setCurrentBlog(null);
        setImagePreview(null);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked, files } = e.target;

        if (type === 'file') {
            const file = files?.[0];
            setFormData({ ...formData, [name]: file || null });
            if (file) {
                setImagePreview(URL.createObjectURL(file));
            }
        } else if (type === 'checkbox') {
            setFormData({ ...formData, [name]: checked });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleDescriptionChange = (content) => {
        setFormData({ ...formData, desc: content });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isEditing && !formData.image) {
            toast.error('Please upload an image for the blog');
            return;
        }

        try {
            setSubmitting(true);

            const data = new FormData();
            if (formData.title) data.append('title', formData.title);
            if (formData.desc) data.append('desc', formData.desc);
            if (formData.image) data.append('image', formData.image);
            if (isEditing) data.append('isActive', formData.isActive);

            let response;
            if (isEditing && currentBlog?._id) {
                response = await updateBlog(currentBlog._id, data);
            } else {
                response = await addBlog(data);
            }

            if (response && response.success) {
                toast.success(`Blog successfully ${isEditing ? 'updated' : 'added'}`);
                fetchBlogs();
                handleCloseModal();
            } else {
                toast.error(response?.message || 'Operation failed');
            }
        } catch (error) {
            toast.error('Error saving blog');
            console.error(error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleStatus = (blog) => {
        setCurrentBlog({
            _id: blog._id,
            actionType: 'ToggleStatus',
            isActive: blog.isActive,
        });
        setConfirm(true);
    };

    const handleDelete = (blogId) => {
        setCurrentBlog({ _id: blogId, actionType: 'Delete' });
        setConfirm(true);
    };

    const onConfirm = () => {
        if (currentBlog?.actionType === 'Delete') {
            performDelete(currentBlog._id);
        } else if (currentBlog?.actionType === 'ToggleStatus') {
            performToggle(currentBlog._id);
        }
    };

    const performDelete = async (id) => {
        try {
            const response = await deleteBlog(id);
            if (response && response.success) {
                toast.success('Blog deleted successfully');
                setConfirm(false);
                fetchBlogs();
            } else {
                toast.error(response?.message || 'Failed to delete blog');
            }
        } catch (error) {
            toast.error('Error deleting blog');
            console.error(error);
        }
    };

    const performToggle = async (id) => {
        try {
            const response = await toggleBlogStatus(id);
            if (response && response.success) {
                toast.success('Blog status toggled successfully');
                setConfirm(false);
                fetchBlogs();
            } else {
                toast.error(response?.message || 'Failed to toggle status');
            }
        } catch (error) {
            toast.error('Error toggling status');
            console.error(error);
        }
    };

    const columns = useMemo(() => [
        {
            name: 'Image',
            cell: (row) => (
                row?.image ? (
                    <img
                        src={row.image}
                        alt={row.title || 'Blog'}
                        className="blog-mgmt-thumb"
                    />
                ) : (
                    <div className="blog-mgmt-thumb blog-mgmt-thumb--empty">
                        <i className="ri-image-line" />
                    </div>
                )
            ),
            maxWidth: '140px',
            minWidth: '100px',
        },
        {
            name: 'Title',
            selector: (row) => row?.title || '-',
            sortable: true,
            wrap: true,
            grow: 2,
            minWidth: '180px',
            cell: (row) => (
                <div className="blog-mgmt-title-cell">
                    <span className="fw-medium">{row?.title || '-'}</span>
                    {row?.slug && (
                        <small className="text-muted d-block text-truncate">{row.slug}</small>
                    )}
                </div>
            ),
        },
        {
            name: 'Date',
            selector: (row) => row?.createdAt || row?.date,
            sortable: true,
            minWidth: '120px',
            cell: (row) => (
                <span className="text-nowrap">
                    {moment(row?.createdAt || row?.date).format('DD MMM YYYY')}
                </span>
            ),
        },
        {
            name: 'Status',
            cell: (row) => (
                <Button
                    onClick={() => handleToggleStatus(row)}
                    title={row?.isActive ? 'Click to deactivate' : 'Click to activate'}
                    className={`btn_status ${row?.isActive ? 'active' : ''}`}
                    style={{ padding: 0, border: 'none', backgroundColor: 'transparent' }}
                >
                    <span
                        className="text-white blog-mgmt-status-badge"
                        style={{ backgroundColor: row?.isActive ? '#198754' : '#6c757d' }}
                    >
                        {row?.isActive ? 'Active' : 'Inactive'}
                    </span>
                </Button>
            ),
            ignoreRowClick: true,
            maxWidth: '120px',
            minWidth: '100px',
        },
        {
            name: 'Actions',
            cell: (row) => (
                <AdminRowActions>
                    <AdminActionButton
                        action="view"
                        label="Preview on store"
                        onClick={() => {
                            const slug = row?.slug || row?._id;
                            window.open(`${Utils.STORE_FRONT_URL}/blog/${slug}`, '_blank', 'noopener,noreferrer');
                        }}
                    />
                    <AdminActionButton action="edit" onClick={() => handleShowModal(row)} />
                    <AdminActionButton action="delete" onClick={() => handleDelete(row._id)} />
                </AdminRowActions>
            ),
            ignoreRowClick: true,
            minWidth: '130px',
        },
    ], []);

    return (
        <React.Fragment>
            <Header />
            <ConfirmModal
                show={confirm}
                onCloseClick={() => setConfirm(false)}
                onConfirm={onConfirm}
                data={currentBlog}
            />
            <div className="main main-app p-3 p-lg-4 blog-mgmt-page">
                {showModal ? (
                    <div className="admin-blog-editor blog-mgmt-editor">
                        <div className="main-page-header d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                            <div>
                                <ol className="breadcrumb fs-sm mb-1">
                                    <li className="breadcrumb-item">
                                        <button type="button" className="btn btn-link p-0 border-0" onClick={handleCloseModal}>
                                            Blog Management
                                        </button>
                                    </li>
                                    <li className="breadcrumb-item active" aria-current="page">
                                        {isEditing ? 'Edit Blog' : 'Create Blog'}
                                    </li>
                                </ol>
                                <h4 className="main-title mb-0 admin-page-title">
                                    {isEditing ? 'Edit Blog Post' : 'Create New Blog Post'}
                                </h4>
                            </div>
                            <Button variant="outline-secondary" size="sm" onClick={handleCloseModal}>
                                <i className="ri-arrow-go-back-line me-1" />
                                Back to List
                            </Button>
                        </div>

                        <Card className="shadow-sm border-0 blog-mgmt-editor-card">
                            <CardBody className="p-3 p-md-4">
                                <Form onSubmit={handleSubmit}>
                                    <Row>
                                        <Col xs={12} lg={8} className="mb-3">
                                            <Form.Group>
                                                <Form.Label>Title <span className="text-danger">*</span></Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="title"
                                                    value={formData.title}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter blog title"
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>

                                        <Col xs={12} lg={4} className="mb-3">
                                            {isEditing && (
                                                <Form.Group>
                                                    <Form.Label>Status</Form.Label>
                                                    <Form.Check
                                                        type="switch"
                                                        id="active-switch"
                                                        label={formData.isActive ? 'Active' : 'Inactive'}
                                                        name="isActive"
                                                        checked={formData.isActive}
                                                        onChange={handleInputChange}
                                                    />
                                                </Form.Group>
                                            )}
                                        </Col>

                                        <Col xs={12} md={6} className="mb-3">
                                            <Form.Group>
                                                <Form.Label>
                                                    Cover Image {isEditing ? '(Optional — upload to replace)' : <span className="text-danger">*</span>}
                                                </Form.Label>
                                                <Form.Control
                                                    type="file"
                                                    name="image"
                                                    accept="image/*"
                                                    onChange={handleInputChange}
                                                    required={!isEditing}
                                                />
                                                <Form.Text className="text-muted">
                                                    Recommended: 1200×630px, JPG or PNG
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>

                                        <Col xs={12} md={6} className="mb-3">
                                            {imagePreview && (
                                                <div className="blog-mgmt-preview-wrap">
                                                    <Form.Label className="d-block">Preview</Form.Label>
                                                    <img
                                                        src={imagePreview}
                                                        alt="Blog preview"
                                                        className="blog-mgmt-preview-img"
                                                    />
                                                </div>
                                            )}
                                        </Col>

                                        <Col xs={12} className="mb-3">
                                            <Form.Group>
                                                <Form.Label>Content <span className="text-danger">*</span></Form.Label>
                                                <div className="blog-mgmt-quill-wrap">
                                                    <ReactQuill
                                                        theme="snow"
                                                        value={formData.desc}
                                                        onChange={handleDescriptionChange}
                                                        modules={QUILL_MODULES}
                                                        placeholder="Write your blog content here..."
                                                    />
                                                </div>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <div className="d-flex flex-wrap gap-2 mt-2 pt-3 border-top">
                                        <Button type="submit" variant="primary" disabled={submitting}>
                                            {submitting ? (
                                                <>
                                                    <Spinner size="sm" className="me-2" />
                                                    Saving...
                                                </>
                                            ) : (
                                                isEditing ? 'Update Blog' : 'Create Blog'
                                            )}
                                        </Button>
                                        <Button type="button" variant="outline-secondary" onClick={handleCloseModal} disabled={submitting}>
                                            Cancel
                                        </Button>
                                    </div>
                                </Form>
                            </CardBody>
                        </Card>
                    </div>
                ) : (
                    <>
                        <div className="main-page-header d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                            <div>
                                <ol className="breadcrumb fs-sm mb-1">
                                    <li className="breadcrumb-item"><Link to="/admin/dashboard">Dashboard</Link></li>
                                    <li className="breadcrumb-item active" aria-current="page">Blog Management</li>
                                </ol>
                                <h4 className="main-title mb-0 admin-page-title">Blog Management</h4>
                            </div>
                            <AdminAddButton label="Create Blog" onClick={() => handleShowModal()} />
                        </div>

                        <Row className="g-3 mb-3 blog-mgmt-stats">
                            <Col xs={6} md={4}>
                                <div className="blog-mgmt-stat-card">
                                    <span className="blog-mgmt-stat-label">Total Blogs</span>
                                    <strong className="blog-mgmt-stat-value">{loading ? '—' : stats.total}</strong>
                                </div>
                            </Col>
                            <Col xs={6} md={4}>
                                <div className="blog-mgmt-stat-card blog-mgmt-stat-card--active">
                                    <span className="blog-mgmt-stat-label">Active</span>
                                    <strong className="blog-mgmt-stat-value">{loading ? '—' : stats.active}</strong>
                                </div>
                            </Col>
                            <Col xs={12} md={4}>
                                <div className="blog-mgmt-stat-card blog-mgmt-stat-card--inactive">
                                    <span className="blog-mgmt-stat-label">Inactive</span>
                                    <strong className="blog-mgmt-stat-value">{loading ? '—' : stats.inactive}</strong>
                                </div>
                            </Col>
                        </Row>

                        <Card className="shadow-sm border-0 blog-mgmt-table-card">
                            <CardBody>
                                <Row className="g-2 mb-3">
                                    <Col xs={12} md={6}>
                                        <div className="admin-search-wrap">
                                            <i className="ri-search-line admin-search-icon" />
                                            <Form.Control
                                                type="search"
                                                placeholder="Search by title or content..."
                                                value={searchText}
                                                disabled={loading}
                                                onChange={(e) => setSearchText(e.target.value)}
                                            />
                                        </div>
                                    </Col>
                                    <Col xs={8} sm={6} md={3}>
                                        <Form.Select
                                            value={filterStatus}
                                            disabled={loading}
                                            onChange={(e) => setFilterStatus(e.target.value)}
                                        >
                                            <option value="all">All Status</option>
                                            <option value="active">Active Only</option>
                                            <option value="inactive">Inactive Only</option>
                                        </Form.Select>
                                    </Col>
                                    <Col xs={4} sm={6} md={3} className="d-flex justify-content-md-end">
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            className="w-100 w-md-auto"
                                            disabled={loading || (!searchText && filterStatus === 'all')}
                                            onClick={() => {
                                                setSearchText('');
                                                setFilterStatus('all');
                                            }}
                                        >
                                            Clear
                                        </Button>
                                    </Col>
                                </Row>

                                {!loading && searchText && (
                                    <p className="text-muted small mb-2">
                                        Showing {filteredBlogs.length} of {blogs.length} blogs
                                    </p>
                                )}

                                <div className={`blog-mgmt-table-wrap ${loading ? '' : 'blog-mgmt-table-wrap--loaded'}`}>
                                    <DataTable
                                        progressPending={loading}
                                        progressComponent={<DataTableSkeleton rows={8} columns={5} />}
                                        columns={columns}
                                        data={filteredBlogs}
                                        pagination
                                        highlightOnHover
                                        striped
                                        responsive
                                        paginationComponentOptions={dataTablePaginationOptions}
                                        customStyles={dataTableCustomStyles}
                                        noDataComponent={
                                            <AdminEmptyState
                                                icon="ri-article-line"
                                                title="No blogs found"
                                                description={
                                                    searchText || filterStatus !== 'all'
                                                        ? 'Try adjusting your search or filters.'
                                                        : 'Get started by creating your first blog post.'
                                                }
                                                actionLabel={!searchText && filterStatus === 'all' ? 'Create Blog' : ''}
                                                onAction={() => handleShowModal()}
                                            />
                                        }
                                        paginationPerPage={10}
                                        paginationRowsPerPageOptions={[10, 20, 30, 50]}
                                    />
                                </div>
                            </CardBody>
                        </Card>
                    </>
                )}
            </div>
            <Footer />
        </React.Fragment>
    );
};

export default BlogEditor;
